/**
 * My details — a salesperson/driver reviews and edits their own profile.
 *
 * Deliberately the SAME six-step wizard the admin uses to add staff (Identity,
 * Family & Emergency, KYC Docs, Address, Bank Details, System Controls), so the
 * fields a person sees here match exactly what the office entered. Pre-filled
 * with what is on file; any change is submitted for admin approval and nothing
 * on the live record moves until it is approved.
 *
 * The one difference from the admin wizard: the "System Controls" step is
 * read-only here. A driver setting their own discount ceiling, access level or
 * code would be a hole — those stay the office's to set.
 */

import { useEffect, useState } from 'react';
import { Page } from '../../components/Shell';
import { Banner, Button, Card, Select, Switch, TextArea, TextInput, useToast } from '../../components/ui';
import { CameraSheet } from '../../components/Camera';
import { type CompressedImage } from '../../lib/camera';
import { useQuery } from '../../lib/useApi';
import { api, postFile, ApiError } from '../../lib/api';
import { phoneInputDigits } from '../../lib/phone';
import { useT } from '../../lib/i18n';

interface MeResponse {
  role: 'salesperson' | 'driver';
  phone: string; accountStatus: string; pendingUpdate: boolean;
  code: string | null; appAccessLevel: string;
  fullName: string | null; email: string | null; altPhone: string | null;
  dateOfBirth: string | null; fatherName: string | null; fatherPhone: string | null;
  nokName: string | null; nokRelationship: string | null; nokPhone: string | null;
  panNumber: string | null; licenceNumber: string | null; licenceExpiry: string | null;
  permanentAddress: string | null; permanentPincode: string | null;
  currentAddress: string | null; currentPincode: string | null;
  bankAccountHolder: string | null; bankNameBranch: string | null;
  bankAccountNumber: string | null; bankIfsc: string | null;
  photoMediaId: string | null; aadhaarFrontMediaId: string | null; aadhaarBackMediaId: string | null;
  panCopyMediaId: string | null; addressProofMediaId: string | null; chequeMediaId: string | null;
  licenceFrontMediaId: string | null; licenceBackMediaId: string | null;
  // Declarations (0066).
  codeOfConductAgreed?: number; signatureOnFile?: number;
  registrationPlace?: string | null; registrationDate?: string | null;
  // Category fields (0066).
  licenceType?: string | null; commercialDrivingYears?: number | null;
  fmcgExperienceMonths?: number | null; usesOwnTwoWheeler?: number;
  personalVehicleRc?: string | null; twoWheelerLicenceNumber?: string | null;
}

const TEXT_KEYS = [
  'fullName', 'email', 'altPhone', 'dateOfBirth', 'fatherName', 'fatherPhone',
  'nokName', 'nokRelationship', 'nokPhone', 'panNumber', 'licenceNumber', 'licenceExpiry',
  'permanentAddress', 'permanentPincode', 'currentAddress', 'currentPincode',
  'bankAccountHolder', 'bankNameBranch', 'bankAccountNumber', 'bankIfsc',
] as const;

const RX = {
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/, ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  pin6: /^\d{6}$/, acc: /^\d{9,18}$/, mobile: /^[6-9]\d{9}$/, email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};
const STEPS = ['Identity', 'Family & Emergency', 'KYC Docs', 'Address', 'Bank Details', 'Role & Declaration', 'System Controls'];
const LAST_STEP = STEPS.length - 1;

export default function MyProfile() {
  const t = useT();
  const toast = useToast();
  const { data } = useQuery<MeResponse>('/api/staff/me');
  const isDriver = data?.role === 'driver';

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [camera, setCamera] = useState(false);

  // New uploads this session (undefined = leave whatever is on file).
  const [photo, setPhoto] = useState<CompressedImage | null>(null);
  const [aadhaarFront, setAadhaarFront] = useState<File | null>(null);
  const [aadhaarBack, setAadhaarBack] = useState<File | null>(null);
  const [panCopy, setPanCopy] = useState<File | null>(null);
  const [addrProof, setAddrProof] = useState<File | null>(null);
  const [cheque, setCheque] = useState<File | null>(null);
  const [licenceFront, setLicenceFront] = useState<File | null>(null);
  const [licenceBack, setLicenceBack] = useState<File | null>(null);

  // Role & Declaration (the new step 6) — were separate cards with their own save
  // buttons; now folded into the wizard and saved by the single Submit below.
  const [licenceType, setLicenceType] = useState('');
  const [commercialYears, setCommercialYears] = useState('');
  const [fmcgMonths, setFmcgMonths] = useState('');
  const [ownTwoWheeler, setOwnTwoWheeler] = useState(false);
  const [personalRc, setPersonalRc] = useState('');
  const [twoWheelerLic, setTwoWheelerLic] = useState('');
  const [coc, setCoc] = useState(false);
  const [signature, setSignature] = useState<File | null>(null);
  const [regPlace, setRegPlace] = useState('');
  const [regDate, setRegDate] = useState('');

  useEffect(() => {
    if (!data) return;
    const d = data as unknown as Record<string, unknown>;
    const f: Record<string, string> = {};
    for (const k of TEXT_KEYS) f[k] = (d[k] as string | null) ?? '';
    setForm(f);
    setLicenceType(data.licenceType ?? '');
    setCommercialYears(data.commercialDrivingYears == null ? '' : String(data.commercialDrivingYears));
    setFmcgMonths(data.fmcgExperienceMonths == null ? '' : String(data.fmcgExperienceMonths));
    setOwnTwoWheeler(data.usesOwnTwoWheeler === 1);
    setPersonalRc(data.personalVehicleRc ?? '');
    setTwoWheelerLic(data.twoWheelerLicenceNumber ?? '');
    setCoc(!!data.codeOfConductAgreed);
    setRegPlace(data.registrationPlace ?? '');
    setRegDate(data.registrationDate ?? '');
  }, [data]);

  const set = (k: string, v: string) => setForm((s) => ({ ...s, [k]: v }));
  const has = (id: string | null | undefined) => !!id;

  function stepOk(): boolean {
    const e: Record<string, string> = {};
    if (step === 0) {
      if (form.email && !RX.email.test(form.email)) e.email = 'Enter a valid email address.';
      if (form.altPhone && !RX.mobile.test(form.altPhone)) e.altPhone = '10 digits, starting 6–9.';
    } else if (step === 1) {
      if (form.fatherPhone && !RX.mobile.test(form.fatherPhone)) e.fatherPhone = '10 digits, starting 6–9.';
      if (form.nokPhone && !RX.mobile.test(form.nokPhone)) e.nokPhone = '10 digits, starting 6–9.';
    } else if (step === 2) {
      if (form.panNumber && !RX.pan.test(form.panNumber)) e.panNumber = 'Format: ABCDE1234F.';
    } else if (step === 3) {
      if (form.permanentPincode && !RX.pin6.test(form.permanentPincode)) e.permanentPincode = 'PIN code is 6 digits.';
      if (form.currentPincode && !RX.pin6.test(form.currentPincode)) e.currentPincode = 'PIN code is 6 digits.';
    } else if (step === 4) {
      if (form.bankAccountNumber && !RX.acc.test(form.bankAccountNumber)) e.bankAccountNumber = 'Account number is 9–18 digits.';
      if (form.bankIfsc && !RX.ifsc.test(form.bankIfsc)) e.bankIfsc = 'Format: SBIN0001234.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function uploadOne(file: File | Blob | null, purpose: string, name: string): Promise<string | undefined> {
    if (!file) return undefined;
    const r = await postFile<{ mediaId: string }>('/api/media/upload', file, name, { purpose });
    return r.mediaId;
  }

  async function submit() {
    if (!stepOk() || !data) return;
    setBusy(true);
    try {
      // Upload any newly chosen documents; keep the existing id otherwise.
      const ids: Record<string, string | undefined> = {
        photoMediaId: (await uploadOne(photo?.blob ?? null, 'employee_photo', 'photo.jpg')) ?? data.photoMediaId ?? undefined,
        aadhaarFrontMediaId: (await uploadOne(aadhaarFront, 'document', aadhaarFront?.name ?? 'aadhaar-front')) ?? data.aadhaarFrontMediaId ?? undefined,
        aadhaarBackMediaId: (await uploadOne(aadhaarBack, 'document', aadhaarBack?.name ?? 'aadhaar-back')) ?? data.aadhaarBackMediaId ?? undefined,
        panCopyMediaId: (await uploadOne(panCopy, 'document', panCopy?.name ?? 'pan')) ?? data.panCopyMediaId ?? undefined,
        addressProofMediaId: (await uploadOne(addrProof, 'document', addrProof?.name ?? 'address-proof')) ?? data.addressProofMediaId ?? undefined,
        chequeMediaId: (await uploadOne(cheque, 'document', cheque?.name ?? 'cheque')) ?? data.chequeMediaId ?? undefined,
        licenceFrontMediaId: (await uploadOne(licenceFront, 'document', licenceFront?.name ?? 'licence-front')) ?? data.licenceFrontMediaId ?? undefined,
        licenceBackMediaId: (await uploadOne(licenceBack, 'document', licenceBack?.name ?? 'licence-back')) ?? data.licenceBackMediaId ?? undefined,
      };
      const payload: Record<string, string> = {};
      for (const k of TEXT_KEYS) payload[k] = (form[k] ?? '').trim();
      for (const [k, v] of Object.entries(ids)) if (v) payload[k] = v;

      await api.post('/api/staff/me/profile-update', payload);

      // Role details (saved directly — the office can still correct them).
      await api.post('/api/staff/me/extended', isDriver
        ? { licenceType: licenceType || null, commercialDrivingYears: commercialYears === '' ? null : Number(commercialYears) }
        : {
            fmcgExperienceMonths: fmcgMonths === '' ? null : Number(fmcgMonths),
            usesOwnTwoWheeler: ownTwoWheeler,
            personalVehicleRc: personalRc.trim() || null,
            twoWheelerLicenceNumber: twoWheelerLic.trim() || null,
          });

      // Declarations (your own — saved directly). Upload a new signature first.
      let signatureMediaId: string | undefined;
      if (signature) {
        const r = await postFile<{ mediaId: string }>('/api/media/upload', signature, signature.name, { purpose: 'document' });
        signatureMediaId = r.mediaId;
      }
      await api.post('/api/staff/me/declarations', {
        codeOfConductAgreed: coc,
        ...(signatureMediaId ? { signatureMediaId } : {}),
        registrationPlace: regPlace.trim() || null,
        registrationDate: regDate || null,
      });

      setSubmitted(true);
      toast.show('Sent to the office for approval.', 'ok');
    } catch (err) {
      if (err instanceof ApiError) {
        toast.show(err.message, 'danger');
        if (err.fields) setErrors(err.fields);
      } else {
        toast.show(t('app.somethingWrong'), 'danger');
      }
    } finally {
      setBusy(false);
    }
  }

  const docBtn = (label: string, file: File | null, setFile: (f: File | null) => void, existing: boolean, sub: string) => (
    <div className="stack-1">
      <div className="t-sm t-semi">{label}</div>
      <label
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '12px 13px', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--ink)',
          border: `1.5px ${file || existing ? 'solid' : 'dashed'} var(${file || existing ? '--ok' : '--line'})`,
          background: file || existing ? 'var(--ok-bg)' : 'var(--bg)',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file ? `✓ ${file.name}` : existing ? '✓ On file' : 'Tap to upload'}
        </span>
        <span className="t-xs t-muted" style={{ marginLeft: 'auto', flex: '0 0 auto' }}>{file || existing ? 'Change' : sub}</span>
        <input
          type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); e.target.value = ''; }}
        />
      </label>
    </div>
  );

  if (submitted) {
    return (
      <Page title={t('app.myDetails')} back>
        <div className="stack-4" style={{ paddingTop: 8 }}>
          <Banner tone="ok" title="Sent for approval">
            <span className="t-sm">
              Your changes have gone to the office. Your current details stay in use until they are approved.
            </span>
          </Banner>
        </div>
      </Page>
    );
  }

  return (
    <Page title={t('app.myDetails')} back>
      {camera && (
        <CameraSheet
          open facing="user" title="Capture photograph"
          onCapture={(img) => { setPhoto(img); setCamera(false); }}
          onClose={() => setCamera(false)}
        />
      )}
      <div className="stack-4">
        {data?.pendingUpdate && (
          <Banner tone="info" title="A change is awaiting approval">
            <span className="t-sm">
              You have details waiting for the office to approve. You can keep working with your current
              details. Submitting again replaces the pending request.
            </span>
          </Banner>
        )}

        <Card className="stack-2">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="t-sm t-muted">Mobile (sign-in)</span>
            <span className="t-sm t-semi">{data?.phone ?? ''}</span>
          </div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="t-sm t-muted">Role</span>
            <span className="t-sm">{isDriver ? 'Driver' : 'Salesperson'}{data?.code ? ` · ${data.code}` : ''}</span>
          </div>
        </Card>

        {/* Progress rail — identical to the admin wizard. */}
        <div className="row" style={{ gap: 2, overflowX: 'auto', paddingBottom: 2 }}>
          {STEPS.map((s, i) => (
            <button
              key={s} type="button" onClick={() => setStep(i)}
              style={{
                flex: '1 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 4, minWidth: 54, background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              }}
            >
              <span style={{
                width: 26, height: 26, borderRadius: '50%', display: 'grid', placeItems: 'center',
                fontSize: 12, fontWeight: 700,
                background: i < step ? 'var(--ok)' : i === step ? 'var(--brand)' : 'var(--bg)',
                color: i <= step ? 'var(--brand-ink)' : 'var(--muted)',
                border: `1.5px solid ${i < step ? 'var(--ok)' : i === step ? 'var(--brand)' : 'var(--line)'}`,
              }}>{i < step ? '✓' : i + 1}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: i === step ? 'var(--ink)' : 'var(--muted)' }}>{s}</span>
            </button>
          ))}
        </div>
        <div className="t-xs t-muted">Step {step + 1} of {STEPS.length}</div>

        <Card>
          {step === 0 && (
            <div className="stack-2">
              <TextInput label="Full name" value={form.fullName ?? ''} error={errors.fullName} onChange={(e) => set('fullName', e.target.value)} />
              <TextInput label="Date of birth" type="date" value={form.dateOfBirth ?? ''} error={errors.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
              <TextInput label="Email address" type="email" value={form.email ?? ''} error={errors.email} onChange={(e) => set('email', e.target.value)} />
              <TextInput label="Alternate mobile" inputMode="numeric" value={form.altPhone ?? ''} error={errors.altPhone} onChange={(e) => set('altPhone', phoneInputDigits(e.target.value))} />
              <div className="stack-1">
                <div className="t-sm t-semi">Photograph</div>
                <button
                  type="button" onClick={() => setCamera(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                    padding: '12px 13px', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--ink)',
                    border: `1.5px ${photo || has(data?.photoMediaId) ? 'solid' : 'dashed'} var(${photo || has(data?.photoMediaId) ? '--ok' : '--line'})`,
                    background: photo || has(data?.photoMediaId) ? 'var(--ok-bg)' : 'var(--bg)',
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{photo ? '✓ Photo captured' : has(data?.photoMediaId) ? '✓ On file' : 'Open camera to capture'}</span>
                  <span className="t-xs t-muted" style={{ marginLeft: 'auto' }}>{photo || has(data?.photoMediaId) ? 'Retake' : 'Live selfie'}</span>
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="stack-2">
              <TextInput label="Father's name" value={form.fatherName ?? ''} error={errors.fatherName} onChange={(e) => set('fatherName', e.target.value)} />
              <TextInput label="Father's phone" inputMode="numeric" value={form.fatherPhone ?? ''} error={errors.fatherPhone} onChange={(e) => set('fatherPhone', phoneInputDigits(e.target.value))} />
              <TextInput label="Next of kin — name" value={form.nokName ?? ''} error={errors.nokName} onChange={(e) => set('nokName', e.target.value)} hint="Person to reach in an emergency" />
              <TextInput label="Relationship" value={form.nokRelationship ?? ''} error={errors.nokRelationship} onChange={(e) => set('nokRelationship', e.target.value)} hint="e.g. Spouse, Brother" />
              <TextInput label="Next of kin — phone" inputMode="numeric" value={form.nokPhone ?? ''} error={errors.nokPhone} onChange={(e) => set('nokPhone', phoneInputDigits(e.target.value))} />
            </div>
          )}

          {step === 2 && (
            <div className="stack-2">
              {docBtn('Aadhaar — front', aadhaarFront, setAadhaarFront, has(data?.aadhaarFrontMediaId), 'Image or PDF')}
              {docBtn('Aadhaar — back', aadhaarBack, setAadhaarBack, has(data?.aadhaarBackMediaId), 'Image or PDF')}
              <TextInput label="PAN number" value={form.panNumber ?? ''} error={errors.panNumber} maxLength={10} onChange={(e) => set('panNumber', e.target.value.toUpperCase().replace(/\s/g, '').slice(0, 10))} hint="Format: ABCDE1234F" style={{ textTransform: 'uppercase' }} />
              {docBtn('PAN card copy', panCopy, setPanCopy, has(data?.panCopyMediaId), 'Image or PDF')}
              {isDriver && (
                <>
                  <div className="t-sm t-semi" style={{ marginTop: 4 }}>Driving licence</div>
                  <TextInput label="Driving Licence Number" value={form.licenceNumber ?? ''} error={errors.licenceNumber} onChange={(e) => set('licenceNumber', e.target.value.toUpperCase())} hint="As printed on the licence" />
                  <TextInput label="Licence Validity Date" type="date" value={form.licenceExpiry ?? ''} error={errors.licenceExpiry} onChange={(e) => set('licenceExpiry', e.target.value)} />
                  {docBtn('Driving licence — front', licenceFront, setLicenceFront, has(data?.licenceFrontMediaId), 'Image or PDF')}
                  {docBtn('Driving licence — back', licenceBack, setLicenceBack, has(data?.licenceBackMediaId), 'Image or PDF')}
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="stack-2">
              <TextArea label="Permanent address" rows={2} value={form.permanentAddress ?? ''} error={errors.permanentAddress} onChange={(e) => set('permanentAddress', e.target.value)} />
              <TextInput label="Permanent PIN code" inputMode="numeric" maxLength={6} value={form.permanentPincode ?? ''} error={errors.permanentPincode} onChange={(e) => set('permanentPincode', e.target.value.replace(/\D/g, '').slice(0, 6))} />
              <TextArea label="Current address (if different)" rows={2} value={form.currentAddress ?? ''} onChange={(e) => set('currentAddress', e.target.value)} />
              <TextInput label="Current PIN code" inputMode="numeric" maxLength={6} value={form.currentPincode ?? ''} error={errors.currentPincode} onChange={(e) => set('currentPincode', e.target.value.replace(/\D/g, '').slice(0, 6))} />
              {docBtn('Address proof', addrProof, setAddrProof, has(data?.addressProofMediaId), 'Aadhaar, voter ID, bill')}
            </div>
          )}

          {step === 4 && (
            <div className="stack-2">
              <TextInput label="Account holder name" value={form.bankAccountHolder ?? ''} error={errors.bankAccountHolder} onChange={(e) => set('bankAccountHolder', e.target.value)} />
              <TextInput label="Bank name & branch" value={form.bankNameBranch ?? ''} error={errors.bankNameBranch} onChange={(e) => set('bankNameBranch', e.target.value)} hint="e.g. SBI — MI Road, Jaipur" />
              <TextInput label="Account number" inputMode="numeric" maxLength={18} value={form.bankAccountNumber ?? ''} error={errors.bankAccountNumber} onChange={(e) => set('bankAccountNumber', e.target.value.replace(/\D/g, '').slice(0, 18))} />
              <TextInput label="IFSC code" value={form.bankIfsc ?? ''} error={errors.bankIfsc} maxLength={11} onChange={(e) => set('bankIfsc', e.target.value.toUpperCase().replace(/\s/g, '').slice(0, 11))} hint="Format: SBIN0001234" style={{ textTransform: 'uppercase' }} />
              {docBtn('Cancelled cheque / passbook', cheque, setCheque, has(data?.chequeMediaId), 'Shows account & IFSC')}
            </div>
          )}

          {step === 5 && (
            <div className="stack-3">
              <div className="t-sm t-semi">Role details</div>
              {isDriver ? (
                <>
                  <Select label="Licence type" value={licenceType} onChange={(e) => setLicenceType(e.target.value)}
                    placeholder="Select type"
                    options={[
                      { value: 'LMV', label: 'LMV — light motor vehicle' },
                      { value: 'HMV', label: 'HMV — heavy motor vehicle' },
                      { value: 'commercial_transport', label: 'Commercial / Transport' },
                    ]} />
                  <TextInput label="Commercial driving experience (years)" inputMode="numeric" value={commercialYears}
                    onChange={(e) => setCommercialYears(e.target.value.replace(/\D/g, '').slice(0, 2))} />
                </>
              ) : (
                <>
                  <TextInput label="FMCG sales experience (months)" inputMode="numeric" value={fmcgMonths}
                    onChange={(e) => setFmcgMonths(e.target.value.replace(/\D/g, '').slice(0, 3))} />
                  <Switch label="Uses own two-wheeler for field visits" checked={ownTwoWheeler} onChange={setOwnTwoWheeler} />
                  {ownTwoWheeler && (
                    <>
                      <TextInput label="Personal vehicle RC number" value={personalRc}
                        onChange={(e) => setPersonalRc(e.target.value.toUpperCase())} />
                      <TextInput label="Two-wheeler licence number" value={twoWheelerLic}
                        onChange={(e) => setTwoWheelerLic(e.target.value.toUpperCase())} />
                    </>
                  )}
                </>
              )}

              <div className="t-sm t-semi" style={{ marginTop: 6 }}>Declarations</div>
              <label className="row" style={{ gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="checkbox" checked={coc} onChange={(e) => setCoc(e.target.checked)}
                  style={{ marginTop: 3, width: 'auto', flex: '0 0 auto' }} />
                <span className="t-sm">Agrees to the company Code of Conduct and policies.</span>
              </label>
              <div className="stack-1">
                <div className="t-sm t-semi">
                  Signature / thumb impression
                  {!!data?.signatureOnFile && !signature && (
                    <span className="t-xs" style={{ color: 'var(--ok)', marginLeft: 8 }}>✓ on file</span>
                  )}
                </div>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 13px', borderRadius: 'var(--radius)', cursor: 'pointer',
                  border: `1.5px ${(signature || data?.signatureOnFile) ? 'solid' : 'dashed'} var(${(signature || data?.signatureOnFile) ? '--ok' : '--line'})`,
                  background: (signature || data?.signatureOnFile) ? 'var(--ok-bg)' : 'var(--bg)',
                }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {signature ? `✓ ${signature.name}` : (data?.signatureOnFile ? 'Replace signature' : 'Tap to upload')}
                  </span>
                  <span className="t-xs t-muted" style={{ marginLeft: 'auto' }}>Photo or scan</span>
                  <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) setSignature(f); e.target.value = ''; }} />
                </label>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextInput label="Date of registration" type="date" value={regDate} onChange={(e) => setRegDate(e.target.value)} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextInput label="Place of registration" value={regPlace} onChange={(e) => setRegPlace(e.target.value)} placeholder="Jaipur" />
                </div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="stack-3">
              <Banner tone="info" title="Set by the office">
                <span className="t-sm">
                  Your code, access level and selling permissions are managed by the office. To change these,
                  contact the admin.
                </span>
              </Banner>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="t-sm t-muted">Code</span><span className="t-sm">{data?.code ?? '—'}</span>
              </div>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="t-sm t-muted">Access level</span>
                <span className="t-sm">{(data?.appAccessLevel ?? 'field').replace(/_/g, ' ')}</span>
              </div>
              <div className="t-xs t-muted">Role, beats &amp; product categories are assigned by the office.</div>
            </div>
          )}
        </Card>

        <div className="row" style={{ gap: 8 }}>
          <Button variant="ghost" className="grow" disabled={busy || step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            {t('app.back')}
          </Button>
          {step < LAST_STEP ? (
            <Button className="grow" onClick={() => { if (stepOk()) setStep((s) => s + 1); }}>Next</Button>
          ) : (
            <Button className="grow" busy={busy} onClick={() => void submit()}>Submit for approval</Button>
          )}
        </div>

      </div>
    </Page>
  );
}
